import { NavigateOptions } from "@tanstack/react-router";

/**
 * Helper function to create document preview navigation options
 */
export interface DocumentPreviewParams {
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
  uploadedBy?: string;
  uploadedAt?: string;
  description?: string;
}

/**
 * Create navigation options for document preview page
 */
export const createDocumentPreviewOptions = (
  params: DocumentPreviewParams
): NavigateOptions => {
  return {
    to: "/document-viewer",
    search: {
      fileUrl: params.fileUrl,
      fileName: params.fileName,
      fileSize: params.fileSize?.toString(),
      uploadedBy: params.uploadedBy,
      uploadedAt: params.uploadedAt,
      description: params.description,
    },
  };
};

/**
 * Get file type from file extension
 */
export const getFileTypeFromUrl = (fileUrl: string): string => {
  const extension = fileUrl.split(".").pop()?.toLowerCase() || "";

  const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
  const videoTypes = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
  const audioTypes = ["mp3", "wav", "ogg", "aac", "flac", "m4a"];
  const documentTypes = ["pdf", "doc", "docx", "txt", "rtf"];
  const spreadsheetTypes = ["xls", "xlsx", "csv"];
  const presentationTypes = ["ppt", "pptx"];
  const archiveTypes = ["zip", "rar", "7z", "tar", "gz"];

  if (imageTypes.includes(extension)) return "image";
  if (videoTypes.includes(extension)) return "video";
  if (audioTypes.includes(extension)) return "audio";
  if (documentTypes.includes(extension)) return "document";
  if (spreadsheetTypes.includes(extension)) return "spreadsheet";
  if (presentationTypes.includes(extension)) return "presentation";
  if (archiveTypes.includes(extension)) return "archive";
  return "unknown";
};

/**
 * Check if file can be previewed inline
 */
export const canPreviewInline = (fileUrl: string): boolean => {
  const fileType = getFileTypeFromUrl(fileUrl);
  const extension = fileUrl.split(".").pop()?.toLowerCase() || "";

  // These file types can be previewed directly in browser
  return (
    fileType === "image" ||
    fileType === "video" ||
    fileType === "audio" ||
    extension === "pdf"
  );
};

/**
 * Format file size to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Get file extension from URL
 */
export const getFileExtension = (fileUrl: string): string => {
  return fileUrl.split(".").pop()?.toLowerCase() || "";
};

/**
 * Get content type/MIME type from file extension
 */
export const getContentType = (ext: string): string => {
  const types: { [key: string]: string } = {
    // Document types
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    doc: "application/msword",
    xls: "application/vnd.ms-excel",
    ppt: "application/vnd.ms-powerpoint",
    odt: "application/vnd.oasis.opendocument.text",
    ods: "application/vnd.oasis.opendocument.spreadsheet",
    odp: "application/vnd.oasis.opendocument.presentation",
    rtf: "application/rtf",
    txt: "text/plain",
    csv: "text/csv",

    // Archive types
    zip: "application/zip",
    rar: "application/vnd.rar",
    "7z": "application/x-7z-compressed",
    tar: "application/x-tar",
    gz: "application/gzip",

    // E-book and markup
    epub: "application/epub+zip",
    md: "text/markdown",
    html: "text/html",
    xml: "application/xml",

    // Image types
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    bmp: "image/bmp",
    webp: "image/webp",
    svg: "image/svg+xml",
    ico: "image/x-icon",

    // Video types
    mp4: "video/mp4",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    webm: "video/webm",
    mkv: "video/x-matroska",

    // Audio types
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    aac: "audio/aac",
    flac: "audio/flac",
    m4a: "audio/mp4",
  };
  return types[ext.toLowerCase()] || "application/octet-stream";
};

/**
 * Generate a preview URL for the document
 */
export const generatePreviewUrl = (
  fileUrl: string,
  baseUrl: string = ""
): string => {
  return `${baseUrl}${fileUrl}`;
};
